import { useState, useEffect, useCallback } from 'react';
import axios from '../../../../configApi/axiosConfig';
import { APIURL } from '../../../../configApi/apiConfig';

// ── Constantes ─────────────────────────────────────────────────────────────
const ESTADO_PAGADO   = ['2', 'PAGADO'];
const ESTADO_PENDIENTE = ['1', 'PENDIENTE'];

const AUTH_HEADERS = (token) => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
});

const AMORTIZACION_STATS_INITIAL = {
    pagadas: 0, impagas: 0, pendientes: 0,
    plazo: 0, totalAbono: 0, diasMora: 0,
    saldoImpagas: 0, fetched: false,
};

// ── Helpers de parsing de respuesta ────────────────────────────────────────
const extractFirstItem = (responseData) => {
    if (Array.isArray(responseData?.data) && responseData.data.length > 0)
        return responseData.data[0];
    if (responseData?.data && typeof responseData.data === 'object')
        return responseData.data;
    if (Array.isArray(responseData) && responseData.length > 0)
        return responseData[0];
    return responseData;
};

const extractArray = (responseData) => {
    if (Array.isArray(responseData))       return responseData;
    if (Array.isArray(responseData?.data)) return responseData.data;
    return [];
};

// ── Helpers de dominio ─────────────────────────────────────────────────────
/** Devuelve true si la cuota ya fue pagada (no debe contar para mora). */
const isPagada = (p) => ESTADO_PAGADO.includes(String(p.Estado));

/**
 * Calcula las estadísticas de las cuotas de amortización:
 * pagadas, impagas, pendientes, totalAbono, saldoImpagas y diasMora.
 */
const calcularEstadisticasCuotas = (productos) => {
    let pagadas = 0, impagas = 0, pendientes = 0, totalAbono = 0, saldoImpagas = 0;

    productos.forEach((p) => {
        const estado = String(p.Estado);
        if (ESTADO_PAGADO.includes(estado)) {
            pagadas++;
        } else if (ESTADO_PENDIENTE.includes(estado) && p.Retraso > 0) {
            impagas++;
            saldoImpagas += Number(p.Saldo || 0);
        } else if (ESTADO_PENDIENTE.includes(estado)) {
            pendientes++;
        }
        if (p.Abono) totalAbono += Number(p.Abono);
    });

    // Días de mora = máximo Retraso entre cuotas NO pagadas con atraso real.
    // Las cuotas ya pagadas conservan su retraso histórico, pero no deben
    // inflar los días de mora actuales del crédito.
    const diasMora = productos.reduce((max, p) => {
        if (isPagada(p)) return max;
        const retraso = Number(p.Retraso || 0);
        return retraso > max ? retraso : max;
    }, 0);

    return { pagadas, impagas, pendientes, totalAbono, saldoImpagas, diasMora };
};

/** Filtra y ordena los artículos de factura: prioriza los marcados como Principal. */
const filtrarArticulosPrincipales = (items) => {
    const principales = items.filter((a) => Number(a.Principal) === 1);
    const candidatos = principales.length > 0
        ? principales
        : items.filter((a) => parseFloat(a.DescuentoPorc || 0) < 100 && parseFloat(a.SubTotal || 0) > 0);

    return [...candidatos].sort((a, b) => parseFloat(b.SubTotal || 0) - parseFloat(a.SubTotal || 0));
};

// ── Hook ───────────────────────────────────────────────────────────────────
export const useDetalleCobranzaData = (data, isOpen, initialEstadoGestion = []) => {
    const [loading,           setLoading]           = useState(false);
    const [selectResultado,   setSelectResultado]   = useState([]);
    const [direccionData,     setDireccionData]     = useState(null);
    const [datosCobranza,     setDatosCobranza]     = useState(null);
    const [historialAPI,      setHistorialAPI]      = useState([]);
    const [refreshKey,        setRefreshKey]        = useState(0);
    const [articulosFactura,  setArticulosFactura]  = useState([]);
    const [estadoGestionData, setEstadoGestionData] = useState(initialEstadoGestion);
    const [amortizacionStats, setAmortizacionStats] = useState(AMORTIZACION_STATS_INITIAL);

    // ── Fetchers ────────────────────────────────────────────────────────────
    const fetchDireccion = useCallback(async (solicitudWeb) => {
        try {
            const response = await axios.get(APIURL.Cbo_GestorDeCobranzasOperativodet(solicitudWeb));
            setDireccionData(extractFirstItem(response.data));
            setSelectResultado(response.data);
        } catch (error) {
            console.error('Error fetching dirección:', error);
        }
    }, []);

    const fetchHistorial = useCallback(async (idCompra, solicitudWeb) => {
        try {
            const response = await axios.get(
                APIURL.getDetalleGestoresCobranzasDetalleOperativoWeb(idCompra, solicitudWeb)
            );
            setDatosCobranza(response.data);
            setHistorialAPI(extractArray(response.data));
        } catch (error) {
            console.error('Error fetching historial cobranza:', error);
            setHistorialAPI([]);
        }
    }, []);

    const fetchArticulos = useCallback(async (idCompra) => {
        try {
            const token    = localStorage.getItem('token');
            const response = await axios.post(
                APIURL.getViewDetalleFacturaOperacion(),
                { idCompra },
                { headers: AUTH_HEADERS(token) }
            );
            const items = Array.isArray(response.data)
                ? response.data
                : extractArray(response.data);

            setArticulosFactura(filtrarArticulosPrincipales(items));
        } catch (error) {
            console.error('Error fetching artículos factura:', error);
        }
    }, []);

    const fetchAmortizacion = useCallback(async (idCompra) => {
        try {
            const token    = localStorage.getItem('token');
            const response = await axios.post(
                APIURL.getViewTablaAmortizacion(),
                { idCompra },
                { headers: AUTH_HEADERS(token) }
            );

            const productos = response.data || [];
            const stats     = calcularEstadisticasCuotas(productos);

            setAmortizacionStats({
                ...stats,
                plazo: productos.length,
                fetched: true,
            });
        } catch (error) {
            console.error('Error fetching amortización:', error);
        }
    }, []);

    // ── Efecto principal de carga ───────────────────────────────────────────
    useEffect(() => {
        if (!isOpen || !data?.idCompra) return;

        const load = async () => {
            setLoading(true);
            try {
                const promises = [];

                if (data.sCre_SolicitudWeb) {
                    promises.push(fetchDireccion(data.sCre_SolicitudWeb));
                    promises.push(fetchHistorial(data.idCompra, data.sCre_SolicitudWeb));
                    promises.push(fetchArticulos(data.idCompra));
                } else {
                    setHistorialAPI([]);
                }

                if (!estadoGestionData?.length) {
                    promises.push(
                        axios.get(APIURL.SelectDato())
                            .then((res) => setEstadoGestionData(res.data.data))
                            .catch((err) => console.error('Error fetching estadoGestion:', err))
                    );
                }

                promises.push(fetchAmortizacion(data.idCompra));
                await Promise.all(promises);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [isOpen, data, refreshKey]);

    const refetch = useCallback(() => setRefreshKey((prev) => prev + 1), []);

    return {
        loading,
        selectResultado,
        direccionData,
        datosCobranza,
        historialAPI,
        amortizacionStats,
        articulosFactura,
        estadoGestionData,
        refetch,
    };
};
